import { isSoftwareWbs, wbsGroupKey } from "./wbsGroups";
import { APP_MODULES } from "../../../../../constants/appModules";

/**
 * Procurement registers rendered under every project, with the path and
 * payload keys each one reads from.
 *
 * No icons here, or anywhere below: a row's icon and colour come from its
 * `key` through constants/moduleVisuals.js (see TreeNodeIcon), so a module
 * looks the same in the tree as in the launcher and the sidebar.
 */
export const PROCUREMENT_CATEGORIES = [
  {
    key: "mrq",
    label: "MRQ",
    pathKey: "plantMRQ",
    apiKey: "mrqs",
  },
  {
    key: "vendorBidEvaluation",
    label: "Vendor Bid Evaluation",
    pathKey: "plantVendorBidEvaluation",
    apiKey: "bids",
  },
  {
    key: "po",
    label: "PO",
    pathKey: "plantPO",
    apiKey: "pos",
  },
  {
    key: "mrv",
    label: "MRV",
    pathKey: "plantMRV",
    apiKey: "mrvs",
  },
  {
    key: "srv",
    label: "SRV",
    pathKey: "plantSRV",
    apiKey: "srvs",
  },
];

/**
 * The listed sections under each project - almost everything below is a
 * placeholder: nothing is fetched for them and clicking one does nothing, so
 * they carry no selection-target entry (node selection ignores ids it doesn't
 * know). They are here to show what a project is meant to hold; each becomes
 * a link as the page behind it arrives.
 *
 * An item with a `path` already has its page: it opens /plant/:plantId/<path>
 * for users licensed for its `module` (see SectionItemNode), and stays a
 * placeholder for everyone else.
 */
export const PROJECT_CONTROL_ITEMS = [
  { key: "dcs", label: "Document Control System" },
  { key: "fcs", label: "Fabrication Control System" },
  { key: "schedule", label: "Schedule" },
  { key: "cost-control", label: "Cost Control" },
  { key: "project-schedule", label: "Project Schedule" },
  { key: "project-cost-control", label: "Project Cost Control" },
  {
    key: "mis",
    label: "MIS - Management Information System",
    path: "mis",
    module: APP_MODULES.MIS,
  },
];

/**
 * The "Dashboard" section. Two of these do have a page already - MCS's
 * Equipment List (Major Equipments) and its BOM Dashboard (Bulk Materials) -
 * and the valve and instrument ones do not: MCS holds valves and instruments
 * in one "Valves & Instrumentation" tab rather than a dashboard each. All
 * four are listed the same way until the dashboards behind them exist.
 */
export const DASHBOARD_ITEMS = [
  { key: "valve-list", label: "Valve List Dashboard" },
  { key: "equipment-list", label: "Equipment List Dashboard" },
  { key: "instrument-list", label: "Instrument List Dashboard" },
  { key: "drawing-bom", label: "Drawing BOM Dashboard" },
];

/** The "AIMS" section - Asset Integrity Management. */
export const AIMS_ITEMS = [
  { key: "plant-data", label: "Plant Data" },
  { key: "maintenance-projects", label: "Maintenance Projects" },
  { key: "modification-projects", label: "Modification Projects" },
  { key: "digital-twin", label: "Digital Twin" },
  { key: "cmms", label: "Computerized Maintenance Management System" },
  { key: "spares", label: "Spares" },
];

/** The "Client Data" section. */
export const CLIENT_DATA_ITEMS = [
  { key: "data-historian", label: "Data Historian" },
];

/**
 * The "Design Automation" section - the tools that produce a discipline's
 * design rather than record it, one row per discipline plus the two that
 * feed them all (Smart P&ID and the Line List it yields).
 *
 * `disciplines` is what a row holds: the WBS of that discipline which the
 * Estimation section leaves out, i.e. the ones written under a legacy or
 * unrecognised type (see designAutomationItemForWbs). Smart P&ID and the
 * Line List name no discipline and stay plain rows.
 */
export const DESIGN_AUTOMATION_ITEMS = [
  { key: "smart-pnid", label: "Smart P&ID" },
  { key: "line-list", label: "Line List" },
  {
    key: "pv-automation",
    label: "Pressure Vessel Automation",
    disciplines: ["pv"],
  },
  {
    key: "piping-automation",
    label: "Piping Automation",
    disciplines: ["pip"],
  },
  {
    key: "structural-automation",
    label: "Structural Automation",
    disciplines: ["struc"],
  },
  {
    // "ga" - General Arrangement - is a legacy discipline with no module of
    // its own; a GA WBS describes the plant's layout, so it is listed here.
    key: "plant-automation",
    label: "Plant Automation",
    disciplines: ["plant", "ga"],
  },
  {
    key: "instrumentation-automation",
    label: "Instrumentation Automation",
    disciplines: ["ins"],
  },
  {
    key: "storage-tank-automation",
    label: "Storage Tank Automation",
    disciplines: ["tank"],
  },
];

/**
 * The Design Automation row a WBS belongs under, or null for one that
 * belongs nowhere here.
 *
 * Only the WBS the Estimation section leaves out are placed: a current type
 * is already listed under its discipline folder there, and listing it twice
 * would say it is two things. What is left are the records written by the
 * automation software (legacy types - "pv", "pip", "struc", "ga") and
 * anything whose type this build doesn't know, which names no discipline and
 * so matches no row.
 */
export const designAutomationItemForWbs = (wbs) => {
  if (!isSoftwareWbs(wbs)) return null;

  const discipline = wbsGroupKey(wbs);
  return (
    DESIGN_AUTOMATION_ITEMS.find((item) =>
      item.disciplines?.includes(discipline),
    ) ?? null
  );
};

/** That row's WBS, out of a plant's list. */
export const designAutomationWbs = (wbsList, itemKey) =>
  (Array.isArray(wbsList) ? wbsList : []).filter(
    (wbs) => designAutomationItemForWbs(wbs)?.key === itemKey,
  );

/** The "Validation" section. */
export const VALIDATION_ITEMS = [
  { key: "validation-software", label: "Validation Software" },
];

/**
 * The sections rendered under a project after Procurement, in this order.
 * Each is a folder of its own: it opens and closes on a click, and comes up
 * closed (see PlantNode).
 */
export const PLACEHOLDER_SECTIONS = [
  {
    key: "project-control",
    label: "Project Control",
    items: PROJECT_CONTROL_ITEMS,
  },
  { key: "dashboard", label: "Dashboard", items: DASHBOARD_ITEMS },
  { key: "aims", label: "AIMS", items: AIMS_ITEMS },
  { key: "client-data", label: "Client Data", items: CLIENT_DATA_ITEMS },
  { key: "validation", label: "Validation", items: VALIDATION_ITEMS },
  {
    key: "design-automation",
    label: "Design Automation",
    items: DESIGN_AUTOMATION_ITEMS,
  },
];
